import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import { apiFetch, buildQueryString } from './client'
import type {
  Todo,
  Section,
  CreateTodoRequest,
  UpdateTodoRequest,
  ApiError,
  TodoListParams,
  Note,
  ReformatJobResultBody,
  ExtractJobResultBody,
  ExtractedTodo,
  TodoPriority,
  Recurrence,
  CreateRecurrenceRequest,
  UpdateRecurrenceRequest,
} from './types'
import { useAsyncJob } from './useAsyncJob'
import { idempotencyKey, todoId, sectionId, noteId } from '../lib/ulid'
import { atEnd } from '../lib/fracidx'

// ─── Todos ────────────────────────────────────────────────────────────────────

export function useTodos(
  params: TodoListParams = {}
): UseQueryResult<Todo[], ApiError> {
  return useQuery({
    queryKey: ['todos', params],
    queryFn: () => {
      const qs = buildQueryString(params as Record<string, string | undefined | null>)
      return apiFetch<Todo[]>(`/todos${qs}`)
    },
  })
}

export function useCreateTodo(): UseMutationResult<
  Todo,
  ApiError,
  CreateTodoRequest
> {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (newTodo: CreateTodoRequest) =>
      apiFetch<Todo>('/todos', {
        method: 'POST',
        body: JSON.stringify(newTodo),
        idempotencyKey: idempotencyKey(),
      }),

    onMutate: async (newTodo) => {
      // Cancel any in-flight queries so optimistic update doesn't get overwritten
      await qc.cancelQueries({ queryKey: ['todos'] })

      // Snapshot all matching queries for rollback
      const prevQueries = qc.getQueriesData<Todo[]>({ queryKey: ['todos'] })

      // Optimistically add the todo to all relevant cached query results
      qc.setQueriesData<Todo[]>({ queryKey: ['todos'] }, (old) => {
        const optimistic: Todo = {
          ...newTodo,
          user_id: 'local',
          status: newTodo.status ?? 'todo',
          date: newTodo.date ?? null,
          section_id: newTodo.section_id ?? null,
          note: newTodo.note ?? null,
          reminder_at: newTodo.reminder_at ?? null,
          priority: newTodo.priority ?? null,
          position: newTodo.position ?? 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }
        return [...(old ?? []), optimistic]
      })

      return { prevQueries }
    },

    onError: (_err, _todo, ctx) => {
      // Roll back on error
      if (ctx?.prevQueries) {
        for (const [queryKey, data] of ctx.prevQueries) {
          qc.setQueryData(queryKey, data)
        }
      }
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['todos'] })
    },
  })
}

export function useUpdateTodo(): UseMutationResult<
  Todo,
  ApiError,
  { id: string } & UpdateTodoRequest
> {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...updates }) =>
      apiFetch<Todo>(`/todos/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
        idempotencyKey: idempotencyKey(),
      }),

    onMutate: async ({ id, ...updates }) => {
      await qc.cancelQueries({ queryKey: ['todos'] })

      const prevQueries = qc.getQueriesData<Todo[]>({ queryKey: ['todos'] })

      qc.setQueriesData<Todo[]>({ queryKey: ['todos'] }, (old) =>
        old?.map((t) => {
          if (t.id !== id) return t
          const { priority, date, section_id, reminder_at, reminder_time, ...rest } = updates
          const next: Todo = { ...t, ...rest, updated_at: new Date().toISOString() }
          if (priority !== undefined) {
            next.priority =
              priority === '' || priority === null ? null : (priority as TodoPriority)
          }
          // The API clears nullable fields via `''`; mirror that to null in the
          // cache so optimistic state matches what the server will return.
          if (date !== undefined) next.date = date === '' ? null : date
          if (section_id !== undefined) next.section_id = section_id === '' ? null : section_id
          if (reminder_at !== undefined) {
            next.reminder_at = reminder_at === '' ? null : reminder_at
            // The server drops the relative rule whenever an instant is sent.
            next.reminder_time = null
            next.reminder_day_offset = null
          }
          if (reminder_time !== undefined) next.reminder_time = reminder_time === '' ? null : reminder_time
          return next
        })
      )

      return { prevQueries }
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.prevQueries) {
        for (const [queryKey, data] of ctx.prevQueries) {
          qc.setQueryData(queryKey, data)
        }
      }
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['todos'] })
    },
  })
}

export function useDeleteTodo(): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/todos/${id}`, { method: 'DELETE' }),

    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['todos'] })

      const prevQueries = qc.getQueriesData<Todo[]>({ queryKey: ['todos'] })

      qc.setQueriesData<Todo[]>({ queryKey: ['todos'] }, (old) =>
        old?.filter((t) => t.id !== id)
      )

      return { prevQueries }
    },

    onError: (_err, _id, ctx) => {
      if (ctx?.prevQueries) {
        for (const [queryKey, data] of ctx.prevQueries) {
          qc.setQueryData(queryKey, data)
        }
      }
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['todos'] })
    },
  })
}

// ─── Rollover ─────────────────────────────────────────────────────────────────

export interface RolloverResponse {
  moved: number
  already_rolled: boolean
}

/**
 * Posts the user's local "today" to the server, which moves all incomplete
 * past-dated todos forward in a single locked transaction. Returns moved=0,
 * already_rolled=true if the server has already rolled this user for `today`.
 */
export function useRolloverTodos(): UseMutationResult<
  RolloverResponse,
  ApiError,
  { today: string }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ today }) =>
      apiFetch<RolloverResponse>('/todos/rollover', {
        method: 'POST',
        body: JSON.stringify({ today }),
      }),
    onSuccess: (res) => {
      if (res.moved > 0) {
        qc.invalidateQueries({ queryKey: ['todos'] })
      }
    },
  })
}

// ─── Sections ─────────────────────────────────────────────────────────────────

export function useSections(): UseQueryResult<Section[], ApiError> {
  return useQuery({
    queryKey: ['sections'],
    queryFn: () => apiFetch<Section[]>('/sections'),
  })
}

export function useCreateSection(): UseMutationResult<
  Section,
  ApiError,
  { name: string; position?: number }
> {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (data) =>
      apiFetch<Section>('/sections', {
        method: 'POST',
        body: JSON.stringify({ id: sectionId(), ...data }),
        idempotencyKey: idempotencyKey(),
      }),

    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['sections'] })
    },
  })
}

export function useDeleteSection(): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/sections/${id}`, { method: 'DELETE' }),

    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['sections'] })
      qc.invalidateQueries({ queryKey: ['todos'] })
    },
  })
}

// ─── Recurrences ──────────────────────────────────────────────────────────────

export function useRecurrences(): UseQueryResult<Recurrence[], ApiError> {
  return useQuery({
    queryKey: ['recurrences'],
    queryFn: () => apiFetch<Recurrence[]>('/recurrences'),
  })
}

export function useCreateRecurrence(): UseMutationResult<
  Recurrence,
  ApiError,
  CreateRecurrenceRequest
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) =>
      apiFetch<Recurrence>('/recurrences', {
        method: 'POST',
        body: JSON.stringify(body),
        idempotencyKey: idempotencyKey(),
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['recurrences'] })
      qc.invalidateQueries({ queryKey: ['todos'] })
    },
  })
}

export function useUpdateRecurrence(): UseMutationResult<
  Recurrence,
  ApiError,
  { id: string } & UpdateRecurrenceRequest
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...updates }) =>
      apiFetch<Recurrence>(`/recurrences/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
        idempotencyKey: idempotencyKey(),
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['recurrences'] })
      qc.invalidateQueries({ queryKey: ['todos'] })
    },
  })
}

export function useDeleteRecurrence(): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/recurrences/${id}`, {
        method: 'DELETE',
        idempotencyKey: idempotencyKey(),
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['recurrences'] })
      qc.invalidateQueries({ queryKey: ['todos'] })
    },
  })
}

// ─── Notes ────────────────────────────────────────────────────────────────────

export function useNotes(): UseQueryResult<Note[], ApiError> {
  return useQuery({
    queryKey: ['notes'],
    queryFn: () => apiFetch<Note[]>('/notes'),
  })
}

export function useCreateNote(): UseMutationResult<
  Note,
  ApiError,
  { title?: string; content?: string } | void
> {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (data) =>
      apiFetch<Note>('/notes', {
        method: 'POST',
        body: JSON.stringify({ id: noteId(), ...(data ?? {}) }),
        idempotencyKey: idempotencyKey(),
      }),

    onSuccess: (note) => {
      // Seed the list so the new note is selectable before the refetch lands.
      qc.setQueryData<Note[]>(['notes'], (old) => (old ? [note, ...old] : [note]))
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

export function useUpdateNote(): UseMutationResult<
  Note,
  ApiError,
  { id: string; title?: string; content?: string }
> {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...patch }) =>
      apiFetch<Note>(`/notes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
        idempotencyKey: idempotencyKey(),
      }),

    onSuccess: (note) => {
      qc.setQueryData<Note[]>(['notes'], (old) =>
        old?.map((n) => (n.id === note.id ? note : n))
      )
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

export function useDeleteNote(): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/notes/${id}`, { method: 'DELETE', idempotencyKey: idempotencyKey() }),

    onSuccess: (_void, id) => {
      qc.setQueryData<Note[]>(['notes'], (old) => old?.filter((n) => n.id !== id))
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

export function useReformatNotesJob() {
  return useAsyncJob<{ text: string }, ReformatJobResultBody>({
    kind: 'reformat',
    submitPath: '/notes/ai/reformat',
  })
}

export function useExtractTasksJob() {
  return useAsyncJob<{ text: string }, ExtractJobResultBody>({
    kind: 'extract',
    submitPath: '/notes/ai/extract',
  })
}

export function useBulkCreateTodos(): UseMutationResult<
  Todo[],
  ApiError,
  { todos: ExtractedTodo[]; defaultDate?: string }
> {
  const qc = useQueryClient()
  const { mutateAsync: createTodo } = useCreateTodo()

  return useMutation({
    mutationFn: async ({ todos, defaultDate }) => {
      const created: Todo[] = []
      for (let i = 0; i < todos.length; i++) {
        const todo = todos[i]
        const result = await createTodo({
          id: todoId(),
          text: todo.text,
          status: 'todo',
          date: todo.date ?? defaultDate ?? null,
          reminder_at: todo.reminder_at ?? null,
          section_id: todo.section_id ?? null,
          priority: todo.priority ?? null,
          position: atEnd(i),
        })
        created.push(result)
      }
      return created
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['todos'] })
    },
  })
}
